import { useDispatch, useSelector } from 'react-redux'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Modal, Form, Input, Select, Spin, Button, Tabs } from 'antd'
import { Icon } from '@iconify/react'
import ProjectMultiSelect, {
    buildProjectSelectOptions,
} from '../../../components/ProjectMultiSelect'

import { debounce } from 'lodash'

// Slices
import {
    createClient,
    updateClient,
    fetchProjectsForClientModal,
    clientsState,
    resetClientModalProjects,
    setClientModalProjectSearch,
    fetchClients,
} from '../clients.slice'
import { getClients } from '../clients.service'

// Components
import { showActionNotification } from '../../../components/notification'
import ClientProfilePictureField, {
    EMPTY_CLIENT_PROFILE,
} from './ClientProfilePictureField'
import ClientPermissionsTab from './ClientPermissionsTab'
import {
    EyeIcon,
    EyeSlashIcon,
} from '../../../assets/icons/login/PasswordIcons'
import {
    findDefaultAvatarOptionById,
    findDefaultAvatarOptionBySrc,
} from '../../../assets/clients/avatar'
import { uploadDefaultAvatarAssetToS3 } from '../utils/clientImageUpload'
import { resolveProfilePictureUrl } from '../../../components/MentionEditor/MentionEditorMentionBlot'

// Styles
import styles from './CreateClientModal.module.css'

// Stable default — an inline [] default creates a new reference every render
// and retriggers effects that depend on it (infinite fetch loop).
const EMPTY_FALLBACK_PROJECTS = []

const CreateClientModal = ({
    open,
    onCancel,
    mode = 'create',
    initialClient,
    fallbackProjects = EMPTY_FALLBACK_PROJECTS,
}) => {
    const dispatch = useDispatch()
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [passwordValue, setPasswordValue] = useState('')
    const [initialProjectIds, setInitialProjectIds] = useState([])
    const [profileState, setProfileState] = useState(EMPTY_CLIENT_PROFILE)
    const [initialProfilePicture, setInitialProfilePicture] = useState(null)
    const [activeEditTab, setActiveEditTab] = useState('details')
    const [resolvedEditClient, setResolvedEditClient] = useState(null)
    const permissionsTabRef = useRef(null)
    const [permissionsSaving, setPermissionsSaving] = useState(false)
    /** Client id we already resolved — prevents re-resolving (and re-fetching) in a loop */
    const resolvedClientIdRef = useRef(null)

    const editClient = resolvedEditClient || initialClient

    const watchedGender = Form.useWatch('gender', form)
    const {
        clientModalListStatus,
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
    } = useSelector(clientsState)

    // Browser-compatible password generator (16 characters)
    const generatePassword = () => {
        const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        const lowercase = 'abcdefghijklmnopqrstuvwxyz'
        const numbers = '0123456789'
        const symbols = '!@#$%^&*'
        const allChars = uppercase + lowercase + numbers + symbols

        // Use browser's crypto API if available, otherwise fallback to Math.random
        const getRandomValue = (max) => {
            if (window.crypto && window.crypto.getRandomValues) {
                const array = new Uint32Array(1)
                window.crypto.getRandomValues(array)
                return array[0] / (0xffffffff + 1)
            }
            return Math.random()
        }

        // Ensure at least one of each required type
        let password =
            uppercase[Math.floor(getRandomValue(1) * uppercase.length)] +
            lowercase[Math.floor(getRandomValue(1) * lowercase.length)] +
            numbers[Math.floor(getRandomValue(1) * numbers.length)] +
            symbols[Math.floor(getRandomValue(1) * symbols.length)]

        // Fill the rest randomly to reach 16 characters
        for (let i = password.length; i < 16; i++) {
            password +=
                allChars[Math.floor(getRandomValue(1) * allChars.length)]
        }

        // Shuffle the password to randomize position of required characters
        password = password
            .split('')
            .sort(() => getRandomValue(1) - 0.5)
            .join('')

        return password
    }

    const handleGeneratePassword = (e) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        const newPassword = generatePassword()

        // Update state first
        setPasswordValue(newPassword)

        // Then set the form field value
        form.setFieldsValue({ password: newPassword })

        // Force a re-render by validating the field
        setTimeout(() => {
            form.validateFields(['password']).catch(() => {})
        }, 10)
    }

    useEffect(() => {
        if (!open) {
            setResolvedEditClient(null)
            resolvedClientIdRef.current = null
            return
        }

        if (mode !== 'edit' || !initialClient?._id) {
            setResolvedEditClient(null)
            resolvedClientIdRef.current = null
            return
        }

        // Already resolved for this client — do not resolve/fetch again,
        // otherwise clients without projects loop the user-list API forever.
        if (resolvedClientIdRef.current === String(initialClient._id)) {
            return
        }
        resolvedClientIdRef.current = String(initialClient._id)

        if (initialClient?.projects?.length) {
            setResolvedEditClient(initialClient)
            return
        }

        if (fallbackProjects.length) {
            setResolvedEditClient({
                ...initialClient,
                projects: fallbackProjects,
            })
        }

        let cancelled = false

        const resolveEditClient = async () => {
            try {
                const search =
                    initialClient?.email || initialClient?.userName || ''
                const { data } = await getClients({
                    skip: 0,
                    limit: 20,
                    search,
                })
                const list = data?.result ?? data?.data ?? []
                const match =
                    list.find(
                        (client) =>
                            String(client?._id) === String(initialClient._id)
                    ) || (list.length === 1 ? list[0] : null)

                const mergedClient = match
                    ? { ...initialClient, ...match }
                    : { ...initialClient }

                const projects =
                    mergedClient?.projects?.length > 0
                        ? mergedClient.projects
                        : fallbackProjects

                if (!cancelled) {
                    setResolvedEditClient({
                        ...mergedClient,
                        projects,
                    })
                }
            } catch {
                if (!cancelled) {
                    setResolvedEditClient({
                        ...initialClient,
                        projects: fallbackProjects,
                    })
                }
            }
        }

        resolveEditClient()

        return () => {
            cancelled = true
        }
    }, [open, mode, initialClient, fallbackProjects])

    useEffect(() => {
        if (open) {
            // Reset client modal projects state
            dispatch(resetClientModalProjects())

            // Fetch initial projects with new API
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                })
            )

            if (mode === 'edit') {
                setInitialProjectIds(
                    (editClient?.projects || [])
                        .map((p) => p?._id)
                        .filter(Boolean)
                )
                form.setFieldsValue({
                    name: editClient?.userName || '',
                    email: editClient?.email || '',
                    gender: editClient?.gender || undefined,
                    projects: (editClient?.projects || []).map(
                        (p) => p?._id
                    ),
                })
                setPasswordValue('')
                const existingPic =
                    resolveProfilePictureUrl(editClient?.profilePicture) ||
                    null
                setInitialProfilePicture(existingPic)
                if (existingPic) {
                    const defaultMatch =
                        findDefaultAvatarOptionBySrc(existingPic)
                    if (defaultMatch) {
                        setProfileState({
                            mode: 'default',
                            defaultAvatarId: defaultMatch.id,
                            previewUrl: defaultMatch.src,
                            uploadedUrl: null,
                            touched: false,
                        })
                    } else {
                        setProfileState({
                            mode: 'upload',
                            previewUrl: existingPic,
                            uploadedUrl: existingPic,
                            defaultAvatarId: null,
                            touched: false,
                        })
                    }
                } else {
                    setProfileState(EMPTY_CLIENT_PROFILE)
                }
            } else if (mode === 'create') {
                setInitialProfilePicture(null)
                setProfileState(EMPTY_CLIENT_PROFILE)
            }
        } else {
            // Reset form when modal closes
            form.resetFields()
            setPasswordValue('')
            setInitialProjectIds([])
            setProfileState(EMPTY_CLIENT_PROFILE)
            setInitialProfilePicture(null)
            setActiveEditTab('details')
            setResolvedEditClient(null)
            dispatch(resetClientModalProjects())
        }
    }, [open, form, dispatch, mode, editClient])

    const mergedProjectOptions = React.useMemo(() => {
        const selectedProjects =
            mode === 'edit' && editClient?.projects?.length
                ? editClient.projects
                : []

        const map = new Map()
        ;[...selectedProjects, ...(clientModalProjectsListData || [])].forEach(
            (project) => {
                const id = project?._id ?? project?.projectId
                if (!id || map.has(id)) return
                map.set(id, project)
            }
        )
        return buildProjectSelectOptions(Array.from(map.values()))
    }, [mode, editClient, clientModalProjectsListData])

    const hasProfileSelection = useCallback((state) => {
        if (state?.mode === 'upload' && state?.uploadedUrl) return true
        if (state?.defaultAvatarId) return true
        return false
    }, [])

    const resolveProfilePictureForSubmit = async () => {
        if (profileState.mode === 'upload') {
            if (profileState.uploadedUrl) return profileState.uploadedUrl
            return undefined
        }

        if (!profileState.defaultAvatarId && profileState.uploadedUrl) {
            return profileState.uploadedUrl
        }

        const option = findDefaultAvatarOptionById(profileState.defaultAvatarId)
        if (!option?.src) return undefined

        try {
            return await uploadDefaultAvatarAssetToS3(
                option.src,
                option.id,
                dispatch
            )
        } catch {
            return undefined
        }
    }

    const handleSubmit = async (values) => {
        setLoading(true)
        try {
            let profilePictureUrl
            const shouldSendProfile =
                mode === 'create' ||
                (mode === 'edit' &&
                    (profileState.touched ||
                        (!initialProfilePicture &&
                            hasProfileSelection(profileState))))

            if (shouldSendProfile) {
                profilePictureUrl = await resolveProfilePictureForSubmit()
                if (
                    mode === 'edit' &&
                    profilePictureUrl &&
                    profilePictureUrl === initialProfilePicture
                ) {
                    profilePictureUrl = undefined
                }
            }

            if (mode === 'edit') {
                const clientId = editClient?._id
                if (!clientId) {
                    showActionNotification({
                        type: 'error',
                        message: 'Client not found',
                    })
                    return
                }

                // NOTE: Keeping payload aligned with existing project update usage in ClientsList
                const selectedProjectIds = (values.projects || []).filter(
                    Boolean
                )
                const removedProjectIds = initialProjectIds.filter(
                    (pid) => !selectedProjectIds.includes(pid)
                )
                const payload = {
                    userName: values.name?.trim(),
                    email: values.email?.trim(),
                    gender: values.gender || undefined,
                    addProjectIds: selectedProjectIds,
                    removeProjectIds: removedProjectIds,
                }
                if (profilePictureUrl) {
                    payload.profilePicture = profilePictureUrl
                }

                const result = await dispatch(
                    updateClient({
                        id: clientId,
                        data: payload,
                    })
                )

                const data = result?.payload?.data
                if (data?.success) {
                    showActionNotification({
                        type: 'success',
                        message: 'Client updated successfully',
                        subMessage: values.name,
                        actionButtonText: 'Undo',
                        notificationKey: 'client-updated',
                    })
                    form.resetFields()
                    onCancel()
                    await dispatch(
                        fetchClients({
                            skip: 0,
                            filter: {},
                            limit: 10,
                            search: '',
                        })
                    )
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            data?.message ||
                            'Failed to update client. Please try again.',
                    })
                }
            } else {
                // Create mode
                const payload = {
                    isExistingUser: false,
                    userName: values.name,
                    email: values.email,
                    password: values.password,
                    gender: values.gender || undefined,
                    projectIds: values.projects || [],
                }
                if (profilePictureUrl) {
                    payload.profilePicture = profilePictureUrl
                }

                const result = await dispatch(createClient(payload))
                const data = result?.payload?.data

                if (data?.success) {
                    const createdClient = data?.result
                    const clientName = createdClient?.userName || values.name

                    // Show notification with undo option
                    showActionNotification({
                        message: 'Client created successfully',
                        subMessage: clientName,
                        actionButtonText: 'Undo',
                        notificationKey: 'client-created',
                    })

                    form.resetFields()
                    setPasswordValue('')
                    onCancel()
                    await dispatch(
                        fetchClients({
                            skip: 0,
                            filter: {},
                            limit: 10,
                            search: '',
                        })
                    )
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            data?.message ||
                            'Failed to create client. Please try again.',
                    })
                }
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'An error occurred. Please try again.',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        setProfileState(EMPTY_CLIENT_PROFILE)
        setInitialProfilePicture(null)
        setActiveEditTab('details')
        setPermissionsSaving(false)
        dispatch(resetClientModalProjects())
        onCancel()
    }

    const handleFooterSubmit = async () => {
        if (loading || permissionsSaving) return

        if (mode === 'edit' && activeEditTab === 'permissions') {
            await permissionsTabRef.current?.save?.()
            return
        }

        form.submit()
    }

    const isPermissionsSaveLoading =
        mode === 'edit' && activeEditTab === 'permissions' && permissionsSaving
    const isFooterLoading = loading || isPermissionsSaveLoading

    const footerSubmitLabel = (() => {
        if (mode === 'edit') {
            if (isFooterLoading) return 'Saving...'
            return activeEditTab === 'permissions'
                ? 'Save Permissions'
                : 'Save Changes'
        }
        return loading ? 'Creating...' : 'Create Client'
    })()

    const handleProjectSearch = debounce((value) => {
        dispatch(setClientModalProjectSearch(value))
        dispatch(
            fetchProjectsForClientModal({
                skip: 0,
                filter: value ? { search: value } : {},
                limit: 10,
                search: value,
            })
        )
    }, 300)

    // Load more projects on scroll
    const handleProjectScroll = (e) => {
        const { target } = e
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        // Check if scrolled to bottom (with 50px threshold)
        if (
            scrollHeight - scrollTop <= clientHeight + 50 &&
            clientModalIsNext &&
            clientModalListStatus !== 'loading'
        ) {
            const nextPage = clientModalProjectPage + 1
            const skip = (nextPage - 1) * 10

            dispatch(
                fetchProjectsForClientModal({
                    skip: skip,
                    filter: clientModalProjectSearch
                        ? { search: clientModalProjectSearch }
                        : {},
                    limit: 10,
                    search: clientModalProjectSearch,
                })
            )
        }
    }

    const renderClientDetailsForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark
            autoComplete="off"
            className="create-client-form"
        >
            <ClientProfilePictureField
                value={profileState}
                onChange={setProfileState}
                gender={watchedGender}
                disabled={loading}
                dispatch={dispatch}
                existingProfilePicture={
                    mode === 'edit' ? editClient?.profilePicture : undefined
                }
            />

            <Form.Item
                label="User Name"
                name="name"
                rules={[
                    {
                        required: true,
                        message: 'Please enter user name',
                    },
                    {
                        min: 2,
                        message: 'User name must be at least 2 characters',
                    },
                ]}
            >
                <Input
                    placeholder="Enter user name"
                    size="large"
                    autoComplete="new-username"
                />
            </Form.Item>

            <Form.Item
                label="Email"
                name="email"
                rules={[
                    {
                        required: true,
                        message: 'Please enter your email address',
                    },
                    {
                        type: 'email',
                        message: 'Please enter a valid email address.',
                    },
                ]}
            >
                <Input
                    type="email"
                    placeholder="Enter email address"
                    size="large"
                    autoComplete="off"
                />
            </Form.Item>

            <Form.Item
                label="Gender"
                name="gender"
                rules={[
                    {
                        required: true,
                        message: 'Please select gender',
                    },
                ]}
            >
                <Select
                    placeholder="Select gender"
                    size="large"
                    options={[
                        { label: 'Male', value: 'Male' },
                        { label: 'Female', value: 'Female' },
                        { label: 'Other', value: 'Other' },
                    ]}
                />
            </Form.Item>

            {mode !== 'edit' && (
                <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                        {
                            required: true,
                            message: 'Please enter your password',
                        },
                        {
                            min: 8,
                            message:
                                'Password must be at least 8 characters long',
                        },
                        {
                            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                            message:
                                'Password must include at least one uppercase letter, one lowercase letter, and one number',
                        },
                    ]}
                >
                    <div className={styles.passwordInputWrapper}>
                        <Input.Password
                            placeholder="Enter password"
                            size="large"
                            autoComplete="new-password"
                            value={passwordValue}
                            onChange={(e) => {
                                setPasswordValue(e.target.value)
                                form.setFieldsValue({
                                    password: e.target.value,
                                })
                            }}
                            iconRender={(visible) =>
                                visible ? (
                                    <div
                                        onClick={() => {}}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <EyeIcon />
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => {}}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <EyeSlashIcon />
                                    </div>
                                )
                            }
                        />
                        <Button
                            type="button"
                            size="small"
                            onClick={handleGeneratePassword}
                            className={styles.generatePasswordButton}
                        >
                            Generate
                        </Button>
                    </div>
                </Form.Item>
            )}

            <Form.Item
                label="Projects"
                name="projects"
                rules={[
                    {
                        required: mode !== 'edit',
                        message: 'Please select at least one project',
                    },
                ]}
            >
                <ProjectMultiSelect
                    variant="form"
                    placeholder="Select projects"
                    options={mergedProjectOptions}
                    showSearch
                    filterOption={false}
                    onSearch={handleProjectSearch}
                    onPopupScroll={handleProjectScroll}
                    loading={clientModalListStatus === 'loading'}
                    page={clientModalProjectPage}
                />
            </Form.Item>
        </Form>
    )

    return (
        <Modal
            title={
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderLeft}>
                        <div className={styles.modalHeaderTitle}>
                            {mode === 'edit' ? 'Edit Client' : 'Create Client'}
                        </div>
                    </div>
                </div>
            }
            open={open}
            onCancel={handleCancel}
            footer={
                <div className={styles.modalFooter}>
                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isFooterLoading}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={isFooterLoading}
                            onClick={handleFooterSubmit}
                            className={`${styles.submitButton} ${
                                isFooterLoading ? styles.submitButtonDisabled : ''
                            }`}
                        >
                            {isFooterLoading ? (
                                <>
                                    <Icon
                                        icon="line-md:loading-loop"
                                        width={16}
                                        height={16}
                                        className={styles.submitSpinner}
                                    />
                                    {footerSubmitLabel}
                                </>
                            ) : (
                                footerSubmitLabel
                            )}
                        </button>
                    </div>
                </div>
            }
            width={mode === 'edit' ? 640 : 580}
            className="create-client-modal"
            destroyOnHidden
            centered
            closeIcon={null}
        >
            <Spin spinning={loading}>
                {mode === 'edit' ? (
                    <Tabs
                        activeKey={activeEditTab}
                        onChange={setActiveEditTab}
                        className={styles.editClientTabs}
                        items={[
                            {
                                key: 'details',
                                label: 'Details',
                                children: renderClientDetailsForm(),
                            },
                            {
                                key: 'permissions',
                                label: 'Permissions',
                                children: (
                                    <ClientPermissionsTab
                                        ref={permissionsTabRef}
                                        supportUserId={editClient?._id}
                                        projects={editClient?.projects ?? []}
                                        onSavingChange={setPermissionsSaving}
                                    />
                                ),
                            },
                        ]}
                    />
                ) : (
                    renderClientDetailsForm()
                )}
            </Spin>
        </Modal>
    )
}

export default CreateClientModal
